import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Container, Paper, Typography } from '@mui/material';
import React, { ReactNode, useEffect, useRef } from 'react';
import { useGetCurrentUserSettings } from '../services/react-query/users/useGetCurrentUserSettings';
import { useUpdateUserCollapse } from '../services/react-query/users/useUpdateUserCollapse';
import { UserSettingsCollapseKeys } from '../types/User';

interface AccordionSectionProps {
  title: string;
  headerElements?: ReactNode;
  children: ReactNode;
  collapseId: UserSettingsCollapseKeys;
  id?: string;
  hasData?: boolean;
}

export default function AccordionSection({ title, headerElements = null, children, collapseId, id, hasData }: AccordionSectionProps): JSX.Element {
  const { data: userSettingsData, isLoading } = useGetCurrentUserSettings();
  const { mutate: updateUserSettings } = useUpdateUserCollapse();
  const accordionRef = useRef<HTMLDivElement | null>(null);
  const showExpand = hasData !== false;

  const isCollapsed = userSettingsData?.settings?.collapse?.[collapseId] || false;

  // Triggers scroll on mount if URL hash matches, since browser doesn't auto-scroll to dynamically rendered elements.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash === `#${id}`) {
      setTimeout(() => {
        accordionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [id]);

  if (isLoading) return <></>;

  const handleAccordionChange = () => {
    if (hasData === false) return;
    const newCollapseState = !isCollapsed;
    updateUserSettings({ collapse: { [collapseId]: newCollapseState } });
  };

  const handleHeaderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent the accordion from collapsing when clicking header elements
    if (headerElements && e.target !== e.currentTarget) {
      e.stopPropagation();
    }
  };

  const shouldShowHeaderActions = !showExpand || !isCollapsed;

  const renderHeaderActions = () => {
    if (!headerElements || !shouldShowHeaderActions) return null;

    return (
      <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
        {headerElements}
      </Box>
    );
  };

  return (
    <Accordion
      ref={accordionRef}
      id={id}
      onChange={handleAccordionChange}
      expanded={showExpand && !isCollapsed}
      sx={{ height: 'fit-content', marginBottom: 2, marginTop: 0 }}
    >
      <AccordionSummary
        component={Box}
        expandIcon={<ExpandMoreIcon sx={{ visibility: showExpand ? 'visible' : 'hidden' }} />}
        sx={{ border: 1, borderColor: '#E3E3E5' }}
        onClick={handleHeaderClick}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', mr: 1 }}>
          <Typography variant="h6" fontWeight="bold" lineHeight="1.5">
            {title}
          </Typography>

          {/* Header Elements (only show if expanded) */}
          {renderHeaderActions()}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
        <Container
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: { xs: 0, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            overflow: 'auto',
          }}
        >
          {children}
        </Container>
      </AccordionDetails>
    </Accordion>
  );
}
